import { Link, Outlet, useLocation } from 'react-router-dom';
import { useFlag } from '../../hooks/useFeatureFlags';
import type { FlagName } from '../../types/feature-flags.generated';

/**
 * Settings shell (task 625) — the grouped-sidebar layout every `/settings/*`
 * page renders inside. It replaces the scattered avatar-menu links with one
 * area organised by *who a concern belongs to*: **You** (personal), **Account**
 * (shared, role-gated), **Billing** (owner-only).
 *
 * This is the foundation the follow-on settings pages build on. To add a page,
 * a later task registers its route under `/settings` in `App.tsx` AND adds one
 * entry to `SETTINGS_NAV` below (plus its `innovate.fe.*.page` flag). An entry
 * renders only when BOTH gates pass:
 *   1. its page flag is on, and
 *   2. the group's role gate passes — `owner` groups need
 *      `innovate.fe.billing.page`, whose role-owner cohort is the owner check
 *      (task 692); no role claim is read here any more.
 * A group with no visible entries does not render — so the Account group stays
 * hidden until tasks 628/629 add its General/Team pages, and Billing collapses
 * to nothing for non-owners.
 *
 * Every flag consulted here is evaluated once, in a fixed order, by
 * `useSettingsFlags()` — mirroring `AppLayout.useNavFlags()` so the hook-call
 * order never changes at runtime (React's rules-of-hooks).
 */

type SettingsRole = 'any' | 'owner';

interface SettingsNavItem {
  name: string;
  href: string;
  flag: FlagName;
}

interface SettingsNavGroup {
  name: string;
  /** Group-level role gate. `owner` groups render only for the account owner. */
  role: SettingsRole;
  items: SettingsNavItem[];
}

/**
 * The settings information architecture. Only pages that exist today have
 * entries; follow-on tasks append theirs:
 *   - Account: General (task 628), Team (task 629)
 *   - Billing: Subscription (task 631), Payment (task 632)
 * Until each lands, its line is simply absent and the entry does not render.
 */
const SETTINGS_NAV: SettingsNavGroup[] = [
  {
    name: 'You',
    role: 'any',
    items: [
      { name: 'Profile', href: '/settings/profile', flag: 'innovate.fe.profile.page' },
      { name: 'Security', href: '/settings/security', flag: 'innovate.fe.settings-security.page' },
      { name: 'Email & Privacy', href: '/settings/email-privacy', flag: 'innovate.fe.email-privacy.page' },
      { name: 'Preferences', href: '/settings/preferences', flag: 'innovate.fe.settings-preferences.page' },
    ],
  },
  {
    name: 'Account',
    role: 'any',
    items: [
      { name: 'General', href: '/settings/account', flag: 'innovate.fe.settings-account.page' },
      { name: 'Team', href: '/settings/team', flag: 'innovate.fe.settings-team.page' },
      // The account's API keys (task 974). Account-scoped, not personal: any
      // active key opens every API-key AI Service the account owns.
      { name: 'API Keys', href: '/settings/api-keys', flag: 'innovate.fe.settings-api-keys.page' },
      // The Archive is account-scoped, not personal: one User's delete is
      // restorable by any of them (task 865).
      { name: 'Archive', href: '/settings/archive', flag: 'innovate.fe.archive.page' },
      // The account's model policy: its own provider keys, allowed providers
      // and Models, and defaults (task 1147).
      { name: 'Models', href: '/settings/account/models', flag: 'innovate.fe.settings-account-models.page' },
    ],
  },
  {
    name: 'Billing',
    role: 'owner',
    items: [
      { name: 'Subscription', href: '/settings/subscription', flag: 'innovate.fe.settings-subscription.page' },
      { name: 'Payment', href: '/settings/payment', flag: 'innovate.fe.settings-payment.page' },
      { name: 'Billing', href: '/settings/billing', flag: 'innovate.fe.settings-billing.page' },
      // The monthly spending limit and its alerts (task 1159). Its own page
      // flag, owner-only cohort in v1, so admins can be admitted later with a
      // flag change rather than a code change. Lives at
      // `/settings/billing/spending`, under Billing's href; activeHref picks
      // the longest match so only one entry lights.
      { name: 'Spending', href: '/settings/billing/spending', flag: 'innovate.fe.spending.page' },
    ],
  },
];

/**
 * Evaluate every settings-sidebar flag in a fixed order and return a lookup.
 * The set and order of `useFlag` calls is constant (SETTINGS_NAV is a
 * module-level constant), so this never violates the rules of hooks.
 */
function useSettingsFlags(): Record<FlagName, boolean> {
  return {
    'innovate.fe.profile.page': useFlag('innovate.fe.profile.page'),
    'innovate.fe.settings-security.page': useFlag('innovate.fe.settings-security.page'),
    'innovate.fe.email-privacy.page': useFlag('innovate.fe.email-privacy.page'),
    'innovate.fe.settings-preferences.page': useFlag('innovate.fe.settings-preferences.page'),
    'innovate.fe.settings-account.page': useFlag('innovate.fe.settings-account.page'),
    'innovate.fe.settings-team.page': useFlag('innovate.fe.settings-team.page'),
    'innovate.fe.settings-api-keys.page': useFlag('innovate.fe.settings-api-keys.page'),
    'innovate.fe.archive.page': useFlag('innovate.fe.archive.page'),
    'innovate.fe.settings-account-models.page': useFlag('innovate.fe.settings-account-models.page'),
    'innovate.fe.settings-subscription.page': useFlag('innovate.fe.settings-subscription.page'),
    'innovate.fe.settings-payment.page': useFlag('innovate.fe.settings-payment.page'),
    'innovate.fe.settings-billing.page': useFlag('innovate.fe.settings-billing.page'),
    'innovate.fe.spending.page': useFlag('innovate.fe.spending.page'),
  } as Record<FlagName, boolean>;
}

/**
 * Active when the current path matches the link on a segment boundary — exact,
 * or the link followed by `/`. Mirrors `AppLayout.isLinkActive` so nested
 * pages still light up their sidebar entry.
 */
function isItemActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * The one entry to highlight: the LONGEST href that matches. Models lives at
 * `/settings/account/models` (task 1147), under General's `/settings/account`,
 * and Spending at `/settings/billing/spending` (task 1159), under Billing's
 * `/settings/billing`, so a plain prefix match would light both.
 */
function activeHref(pathname: string, hrefs: string[]): string | null {
  return hrefs.filter((href) => isItemActive(pathname, href)).sort((a, b) => b.length - a.length)[0] ?? null;
}

export default function SettingsLayout() {
  const location = useLocation();
  const flags = useSettingsFlags();

  // Billing affordances are gated by innovate.fe.billing.page, whose
  // role-owner cohort is the owner check (task 692). The role claim is no
  // longer read here: evaluation is uniform and, as a permission-type flag,
  // fails closed during an Unleash outage.
  const canManageBilling = useFlag('innovate.fe.billing.page');

  const visibleGroups = SETTINGS_NAV.map((group) => {
    const rolePasses = group.role === 'owner' ? canManageBilling : true;
    const items = rolePasses ? group.items.filter((item) => flags[item.flag]) : [];
    return { group, items };
  }).filter(({ items }) => items.length > 0);
  const currentHref = activeHref(
    location.pathname,
    visibleGroups.flatMap(({ items }) => items.map((item) => item.href)),
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Your profile, account, and billing — one place, grouped by who it belongs to.
        </p>
      </div>

      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <aside className="w-full flex-shrink-0 lg:sticky lg:top-6 lg:w-56" aria-label="Settings navigation">
          {visibleGroups.map(({ group, items }) => (
            <div key={group.name} className="mb-5" data-testid={`settings-nav-group-${group.name.toLowerCase()}`}>
              <h2 className="px-2.5 pb-1.5 text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                {group.name}
              </h2>
              <nav className="space-y-0.5">
                {items.map((item) => {
                  const active = item.href === currentHref;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      data-testid={`settings-nav-item-${item.href.split('/').pop()}`}
                      aria-current={active ? 'page' : undefined}
                      className={`block rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors ${
                        active
                          ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
          ))}
        </aside>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
